import { useMemo, useReducer, useEffect } from 'react';

import { Input } from '~/components';

import { inputStyle } from '..';

import { calculateTotal } from '../calculateTotalSide';
import { SideValuesAction, SideValuesState, SkinFoldData } from '../types';
import { extractSideMember } from '../utils';

import {
  DoubleInputContainer,
  FieldSet,
  FormContainer,
  Label,
} from '../styles';

type Props = {
  data: SkinFoldData;
};

function reducer(
  state: SideValuesState,
  action: SideValuesAction,
): SideValuesState {
  switch (action.key) {
    case 'replace':
      return action.value as unknown as SideValuesState;
    case 'biceps':
      if (Number.isNaN(action.value)) {
        return { ...state, biceps: 0 };
      }
      return { ...state, biceps: action.value as number };
    case 'triceps':
      if (Number.isNaN(action.value)) {
        return { ...state, triceps: 0 };
      }
      return { ...state, triceps: action.value as number };
    case 'abdomen':
      if (Number.isNaN(action.value)) {
        return { ...state, abdomen: 0 };
      }
      return { ...state, abdomen: action.value as number };
    case 'middle_armpit':
      if (Number.isNaN(action.value)) {
        return { ...state, middle_armpit: 0 };
      }
      return { ...state, middle_armpit: action.value as number };
    case 'breastplate':
      if (Number.isNaN(action.value)) {
        return { ...state, breastplate: 0 };
      }
      return { ...state, breastplate: action.value as number };
    case 'calf':
      if (Number.isNaN(action.value)) {
        return { ...state, calf: 0 };
      }
      return { ...state, calf: action.value as number };
    case 'subscapular':
      if (Number.isNaN(action.value)) {
        return { ...state, subscapular: 0 };
      }
      return { ...state, subscapular: action.value as number };
    case 'heigsupra_iliacaht':
      if (Number.isNaN(action.value)) {
        return { ...state, heigsupra_iliacaht: 0 };
      }
      return { ...state, heigsupra_iliacaht: action.value as number };
    case 'thigh':
      if (Number.isNaN(action.value)) {
        return { ...state, thigh: 0 };
      }
      return { ...state, thigh: action.value as number };
    default:
      return state;
  }
}

const LeftSide = ({ data }: Props) => {
  const initialState: SideValuesState = {
    biceps: String(data.skin_folds_biceps_left) || '0',
    triceps: String(data.skin_folds_triceps_left) || '0',
    subscapular: String(data.skin_folds_subscapular_left) || '0',
    middle_armpit: String(data.skin_folds_middle_armpit_left) || '0',
    heigsupra_iliacaht: String(data.skin_folds_heigsupra_iliacaht_left) || '0',
    abdomen: String(data.skin_folds_abdomen_left) || '0',
    breastplate: String(data.skin_folds_breastplate_left) || '0',
    thigh: String(data.skin_folds_thigh_left) || '0',
    calf: String(data.skin_folds_calf_left) || '0',
    studentAge: data.studentAge,
  };

  const [sideValues, dispatch] = useReducer(reducer, initialState);

  const totalSideValue = useMemo(
    () => calculateTotal(sideValues),
    [sideValues],
  );

  const handleChangeMember = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { id } = event.target;

    const sideMember = extractSideMember(id, 'left');

    const sanitizedValue = event.target.value
      .replace(/[^0-9.,]/g, '')
      .replaceAll('.', ',');

    if (sideMember) {
      dispatch({
        key: sideMember as keyof SideValuesState,
        value: sanitizedValue,
      });
    }
  };

  useEffect(() => {
    const newData: SideValuesState = {
      biceps: data.skin_folds_biceps_left || '0',
      triceps: data.skin_folds_triceps_left || '0',
      subscapular: data.skin_folds_subscapular_left || '0',
      middle_armpit: data.skin_folds_middle_armpit_left || '0',
      heigsupra_iliacaht: data.skin_folds_heigsupra_iliacaht_left || '0',
      abdomen: data.skin_folds_abdomen_left || '0',
      breastplate: data.skin_folds_breastplate_left || '0',
      thigh: data.skin_folds_thigh_left || '0',
      calf: data.skin_folds_calf_left || '0',
      studentAge: data.studentAge,
    };
    dispatch({ key: 'replace', value: newData });
  }, [data]);

  return (
    <FormContainer>
      <FieldSet>
        <DoubleInputContainer>
          <div>
            <Label>Bíceps (mm):</Label>
            <Input
              name="skin_folds_biceps_left"
              id="skin_folds_biceps_left"
              placeholder="Lado esquerdo"
              containerStyle={inputStyle.doubleInput}
              value={sideValues.biceps}
              onChange={handleChangeMember}
              fontSize="1.5rem"
            />
          </div>
          <div>
            <Label>Tríceps (mm):</Label>
            <Input
              name="skin_folds_triceps_left"
              id="skin_folds_triceps_left"
              placeholder="Lado esquerdo"
              containerStyle={inputStyle.doubleInput}
              value={sideValues.triceps}
              onChange={handleChangeMember}
              fontSize="1.5rem"
            />
          </div>
        </DoubleInputContainer>
      </FieldSet>

      <FieldSet>
        <DoubleInputContainer>
          <div>
            <Label>Subescapular (mm):</Label>
            <Input
              name="skin_folds_subscapular_left"
              id="skin_folds_subscapular_left"
              placeholder="Lado esquerdo"
              containerStyle={inputStyle.doubleInput}
              value={sideValues.subscapular}
              onChange={handleChangeMember}
              fontSize="1.5rem"
            />
          </div>
          <div>
            <Label>Axila média (mm):</Label>
            <Input
              name="skin_folds_middle_armpit_left"
              id="skin_folds_middle_armpit_left"
              placeholder="Lado esquerdo"
              containerStyle={inputStyle.doubleInput}
              value={sideValues.middle_armpit}
              onChange={handleChangeMember}
              fontSize="1.5rem"
            />
          </div>
        </DoubleInputContainer>
      </FieldSet>

      <FieldSet>
        <DoubleInputContainer>
          <div>
            <Label>Supra ilíaca (mm):</Label>
            <Input
              name="skin_folds_heigsupra_iliacaht_left"
              id="skin_folds_heigsupra_iliacaht_left"
              placeholder="Lado esquerdo"
              containerStyle={inputStyle.doubleInput}
              value={sideValues.heigsupra_iliacaht}
              onChange={handleChangeMember}
              fontSize="1.5rem"
            />
          </div>
          <div>
            <Label>Abdomen (mm):</Label>
            <Input
              name="skin_folds_abdomen_left"
              id="skin_folds_abdomen_left"
              placeholder="Lado esquerdo"
              containerStyle={inputStyle.doubleInput}
              value={sideValues.abdomen}
              onChange={handleChangeMember}
              fontSize="1.5rem"
            />
          </div>
        </DoubleInputContainer>
      </FieldSet>

      <FieldSet>
        <DoubleInputContainer>
          <div>
            <Label>Peitoral (mm):</Label>
            <Input
              name="skin_folds_breastplate_left"
              id="skin_folds_breastplate_left"
              placeholder="Lado esquerdo"
              containerStyle={inputStyle.doubleInput}
              value={sideValues.breastplate}
              onChange={handleChangeMember}
              fontSize="1.5rem"
            />
          </div>
          <div>
            <Label>Coxa (mm):</Label>
            <Input
              name="skin_folds_thigh_left"
              id="skin_folds_thigh_left"
              placeholder="Lado esquerdo"
              containerStyle={inputStyle.doubleInput}
              value={sideValues.thigh}
              onChange={handleChangeMember}
              fontSize="1.5rem"
            />
          </div>
        </DoubleInputContainer>
      </FieldSet>

      <FieldSet>
        <Label>Panturrilha (mm):</Label>
        <Input
          name="skin_folds_calf_left"
          id="skin_folds_calf_left"
          placeholder="Lado esquerdo"
          containerStyle={inputStyle.doubleInput}
          value={sideValues.calf}
          onChange={handleChangeMember}
          fontSize="1.5rem"
        />
      </FieldSet>

      <FieldSet>
        <Label>Total lado esquerdo (mm):</Label>
        <Input
          disabled
          name="skin_folds_total"
          containerStyle={inputStyle.input}
          fontSize="1.5rem"
          value={totalSideValue}
        />
      </FieldSet>
    </FormContainer>
  );
};

export default LeftSide;
